import React, { FC, ReactElement, ReactNode, useContext, useLayoutEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMeasure, useWindowSize } from 'react-use';
import ThemeContext from '../../contexts/themeContext';
import Button, { IButtonProps } from '../../components/bootstrap/Button';
import Portal from '../Portal/Portal';
import useDarkMode from '../../hooks/useDarkMode';
import Logo from '../../components/Logo';
import SettingsHeader from '../../pages/common/Headers/SettingsHeader';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import LogoKeeplin from '../../components/LogoKeeplin';
import { SUPPORT_EMAIL, SUPPORT_MAILTO_LINK } from '../../common/data/supportConfig';

interface IHeaderLeftProps {
	children: ReactNode;
	className?: string;
}
export const HeaderLeft: FC<IHeaderLeftProps> = ({ children, className }) => {
	return <div className={classNames('header-left', 'col-md', className)}>{children}</div>;
};
HeaderLeft.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
HeaderLeft.defaultProps = {
	className: undefined,
};

interface IHeaderRightProps {
	children: ReactNode;
	className?: string;
}
export const HeaderRight: FC<IHeaderRightProps> = ({ children, className }) => {
	const [ref, { height }] = useMeasure<HTMLDivElement>();

	const root = document.documentElement;
	root.style.setProperty('--header-right-height', `${height}px`);

	return (
		<div ref={ref} className={classNames('header-right', 'col-md-auto', className)}>
			{children}
		</div>
	);
};
HeaderRight.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
HeaderRight.defaultProps = {
	className: undefined,
};

interface IHeaderProps {
	children: ReactElement<IHeaderLeftProps> | ReactElement<IHeaderRightProps> | ReactNode;
	hasLeftMobileMenu?: boolean;
	hasRightMobileMenu?: boolean;
}
const Header: FC<IHeaderProps> = ({ children, hasLeftMobileMenu, hasRightMobileMenu }) => {
	const { themeStatus } = useDarkMode();
	const windowsWidth = useWindowSize().width;
	const [refMobileHeader, sizeMobileHeader] = useMeasure<HTMLDivElement>();
	const [refHeader, sizeHeader] = useMeasure<HTMLDivElement>();

	const root = document.documentElement;
	root.style.setProperty('--mobile-header-height', `${sizeMobileHeader.height}px`);
	root.style.setProperty('--header-height', `${sizeHeader.height}px`);

	const {
		asideStatus,
		setAsideStatus,
		leftMenuStatus,
		setLeftMenuStatus,
		rightMenuStatus,
		setRightMenuStatus,
	} = useContext(ThemeContext);

	useLayoutEffect(() => {
		if (
			(asideStatus || leftMenuStatus || rightMenuStatus) &&
			windowsWidth < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)
		)
			document.body.classList.add('overflow-hidden');
		return () => {
			document.body.classList.remove('overflow-hidden');
		};
	});

	const [notificationsModalOpen, setNotificationsModalOpen] = React.useState(false);
	const [isSupportHovered, setIsSupportHovered] = React.useState(false);

	const { notificationsCount } = useSelector((state: RootState) => state.notifications);
	const styledBtn: IButtonProps = {
		color: themeStatus,
		hoverShadow: 'default',
		isLight: asideStatus,
		size: 'lg',
		className: 'btn-only-icon',
	};

	const LogoComponent = process.env.REACT_APP_LOGO === 'Keeplin' ? LogoKeeplin : Logo;

	return (
		<>
			<header ref={refMobileHeader} className='mobile-header'>
				<div className='container-fluid'>
					<div className='row'>
						<div className='col'>
							<Button
								aria-label='Toggle Aside'
								className='mobile-header-toggle me-3'
								size='lg'
								color={asideStatus ? 'secondary' : themeStatus}
								isLight={asideStatus}
								icon={asideStatus ? 'Close' : 'Menu'}
								onClick={() => {
									setAsideStatus(!asideStatus);
									setLeftMenuStatus(false);
									setRightMenuStatus(false);
								}}
							/>
							<LogoComponent width={120} />
						</div>
						{hasRightMobileMenu && (
							<div className='col-auto d-flex align-items-center'>
								<Button
									aria-label='Toggle Left Menu'
									className='btn-only-icon'
									size='lg'
									color={themeStatus}
									isLight={leftMenuStatus}
									icon='Search'
									onClick={() => {
										setAsideStatus(false);
										setLeftMenuStatus(!leftMenuStatus);
										setRightMenuStatus(false);
									}}
								/>
								<Button
									aria-label='Toggle Left Menu'
									className='btn-only-icon mx-2'
									size='lg'
									color={themeStatus}
									isLight={rightMenuStatus}
									icon='NotificationsActive'
									onClick={() => {
										setNotificationsModalOpen(!notificationsModalOpen);
									}}>
									{notificationsCount > 0 && (
										<span className='position-absolute top-0 start-85 translate-middle badge rounded-pill bg-danger'>
											{notificationsCount}
										</span>
									)}
								</Button>
								<div
									style={{ position: 'relative', display: 'inline-block' }}
									onMouseEnter={() => setIsSupportHovered(true)}
									onMouseLeave={() => setIsSupportHovered(false)}>
									<a
										href={SUPPORT_MAILTO_LINK}
										aria-label={`Contact us: ${SUPPORT_EMAIL}`}
										className='btn btn-only-icon mx-2'>
										<img
											src={`${process.env.PUBLIC_URL}/helpicon.png`}
											alt='Support'
											style={{
												width: '1.7rem',
												height: '1.7rem',
												filter: 'brightness(0) contrast(2)',
											}}
										/>
									</a>
									{isSupportHovered && (
										<>
											<div
												style={{
													position: 'absolute',
													top: '100%',
													right: 0,
													width: 220,
													height: 10,
													pointerEvents: 'auto',
												}}
												onMouseEnter={() => setIsSupportHovered(true)}
												onMouseLeave={() => setIsSupportHovered(false)}
											/>
											<div
												style={{
													position: 'absolute',
													top: 'calc(100% + 10px)',
													right: 0,
													background: '#fff',
													padding: '8px 14px',
													borderRadius: 8,
													fontSize: 13,
													fontWeight: 600,
													whiteSpace: 'nowrap',
													border: '1px solid #f3c9d6',
													boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
													zIndex: 1000,
													pointerEvents: 'auto',
												}}
												onMouseEnter={() => setIsSupportHovered(true)}
												onMouseLeave={() => setIsSupportHovered(false)}>
												<span style={{ color: '#111827' }}>Contact us: </span>
												<a
													href={SUPPORT_MAILTO_LINK}
													style={{
														color: '#f00d69',
														textDecoration: 'underline',
														cursor: 'pointer',
													}}>
													{SUPPORT_EMAIL}
												</a>
												<div
													style={{
														position: 'absolute',
														top: -6,
														right: 14,
														width: 10,
														height: 10,
														background: '#fff',
														borderLeft: '1px solid #f3c9d6',
														borderTop: '1px solid #f3c9d6',
														transform: 'rotate(45deg)',
													}}
												/>
											</div>
										</>
									)}
								</div>
								<SettingsHeader styledBtn={styledBtn} />
							</div>
						)}
					</div>
				</div>
			</header>

			<header
				ref={refHeader}
				className={classNames('header', {
					'header-left-open': leftMenuStatus,
					'header-right-open': rightMenuStatus,
				})}>
				<div className='container-fluid'>
					<div className='row d-flex align-items-center'>
						{children}
						{(leftMenuStatus || rightMenuStatus) && (
							<Portal>
								<div
									role='presentation'
									className={classNames('header-overlay', {
										'header-overlay-left-menu': leftMenuStatus,
										'header-overlay-right-menu': rightMenuStatus,
									})}
									onClick={() => {
										setAsideStatus(false);
										setLeftMenuStatus(false);
										setRightMenuStatus(false);
									}}
								/>
							</Portal>
						)}
					</div>
				</div>
			</header>
		</>
	);
};
Header.propTypes = {
	children: PropTypes.node.isRequired,
	hasLeftMobileMenu: PropTypes.bool,
	hasRightMobileMenu: PropTypes.bool,
};
Header.defaultProps = {
	hasLeftMobileMenu: true,
	hasRightMobileMenu: true,
};

export default Header;